// RegionParams — Phase 7 (plan §6): the per-region params texture.  `regionModulated` params
// fetch their value from this R32F DataTexture (one ROW per assignment region; column = the
// param's fixed slot) instead of a uniform — a region costs a few floats in the DB + one texel
// fetch, so hundreds of regions is a non-issue.
//
// Column assignment MUST match the assembler's (ShaderAssembler.assembleMaterialShader builds
// `regionParamColumns` in the SAME order: per-recipe, params in declaration order, skipping
// non-regionModulated; scalar = 1 column, color = 3).  A region with an absent key falls back
// to the param's resolved default.  Ramps are NOT regionModulated-able (scalar/color only).
//
// Set12 P3 — the FEATHER block: the assembler's `setRegionIndex` reads the per-region row's
// first REGION_FEATHER_BLOCK_COLUMNS columns for the region's feather/curve/noise/opaque (the
// P3 shader's per-fragment feather inputs).  `packRegionParamsWithFeather` prepends that block
// by writing into the reserved columns — `packRegionParams` here leaves them 0 (the fallback
// for recipes without a region).
//
// NOTE (P6): MASKS are NOT packed here — the P6 rasteriser BAKES each sample's composite mask
// factor into the stored signed distance (the inverse-smoothstep bake, `maskedDistance`), so
// the shader needs no mask data and the region-params row layout is UNCHANGED from P5 (a
// maskless region is byte-identical — the bake is the identity for factor 1).  See
// `MaterialRegions.ts` + `SplatRasterizer.ts` (P6 §3.5/§4.5 — "multiply weight" done on the
// rasteriser, per sample, where the elevation/slope lattices live).
import {
	type ParamKind, type ParamValue,
} from "../@MaterialTypes.js";
import type {ResolvedParam, ResolvedRecipe} from "../RecipeResolverCore.js";
import {REGION_FEATHER_BLOCK_COLUMNS, REGION_FEATHER_COLUMN, REGION_FEATHER_CURVE_COLUMN, REGION_NOISE_AMP_COLUMN, REGION_NOISE_SCALE_COLUMN, REGION_OPAQUE_COLUMN} from "../Splat/SplatTypes.js";
import {MaterialBlendDefaults} from "../@MaterialFeature.js";
import type {MaterialRegion} from "../Splat/MaterialRegions.js";
// re-export for the renderer + tests
export {REGION_FEATHER_BLOCK_COLUMNS, REGION_FEATHER_COLUMN, REGION_FEATHER_CURVE_COLUMN, REGION_NOISE_AMP_COLUMN, REGION_NOISE_SCALE_COLUMN, REGION_OPAQUE_COLUMN};

/** Column layout for one recipe's regionModulated params (mirror of the assembler's). */
export interface RegionParamColumnLayout {
	/** Raw param key (per-recipe; the texture is keyed by (region row, column)). */
	paramKey: string;
	kind: ParamKind;
	/** First column of this param in the region's row. */
	column: number;
	/** Float span: scalar = 1, color = 3. */
	span: number;
}

/** Build the region-param column layout for a recipe set — MUST match the assembler's
 *  `regionParamColumns` construction (same order, same skipping).  Throws on a ramp (not
 *  regionModulated-able) or column-budget overflow. */
export function buildRegionParamLayouts(recipes: ResolvedRecipe[], columns: number): RegionParamColumnLayout[] {
	return buildRegionParamLayoutsOffset(recipes, columns, 0);
}

/** Set12 P3 — the P3 region-row layout prepends the fixed FEATHER block (feather, curve,
 *  noiseAmp, noiseScale, opaque — REGION_FEATHER_BLOCK_COLUMNS columns; the assembler's
 *  `setRegionIndex` reads them), so the regionModulated param columns start at that offset.
 *  MUST match the assembler's construction (it starts its regionParamColumns counter at
 *  REGION_FEATHER_BLOCK_COLUMNS when useRegionParams). */
export function buildRegionParamLayoutsForFeather(recipes: ResolvedRecipe[], columns: number): RegionParamColumnLayout[] {
	return buildRegionParamLayoutsOffset(recipes, columns, REGION_FEATHER_BLOCK_COLUMNS);
}

function buildRegionParamLayoutsOffset(recipes: ResolvedRecipe[], columns: number, startColumn: number): RegionParamColumnLayout[] {
	const layouts: RegionParamColumnLayout[] = [];
	let col = startColumn;
	for (const r of recipes) {
		for (const p of r.params) {
			if (!p.regionModulated) continue;
			if (p.kind == "ramp") throw new Error(`[RegionParams] regionModulated ramp param "${p.key}" is not supported (scalar/color only)`);
			const span = p.kind == "color" ? 3 : 1;
			if (col + span > columns) throw new Error(`[RegionParams] regionModulated params exceed the column budget (${columns})`);
			layouts.push({paramKey: p.key, kind: p.kind, column: col, span});
			col += span;
		}
	}
	return layouts;
}

/** Pack the per-region params texture payload: `rows` rows (one per assignment region) ×
 *  `columns` float columns.  Each region supplies `values` keyed by the param's RAW key
 *  (per-recipe — a region belongs to ONE recipe); absent keys use the recipe's resolved default.
 *  Non-regionModulated params don't appear in the texture at all.  `rowOffset` = the FIRST row's
 *  index in the region payload (P2 Set12: the region payload rows are the FULL map region list —
 *  the per-chunk params texture rows must align with the region indices, so a chunk whose
 *  bbox-filtered regions start at map-region 3 writes its params rows at [3, 3 + rows)). */
export function packRegionParams(
	recipes: ResolvedRecipe[],
	rows: number,
	columns: number,
	regionValues: Array<{[paramKey: string]: ParamValue}>,
	rowOffset = 0,
): Float32Array {
	if (rows < 1) throw new Error("[RegionParams] need at least one row (region)");
	if (columns < 1) throw new Error("[RegionParams] need at least one column");
	if (rowOffset < 0) throw new Error("[RegionParams] rowOffset must be >= 0");
	const layouts = buildRegionParamLayoutsForFeather(recipes, columns);
	const out = new Float32Array((rowOffset + rows) * columns);
	// The texture is row-major: row r's params occupy [r*columns, (r+1)*columns).
	// Fill each row from its recipe's resolved defaults, then overlay the region's values.
	for (let r = 0; r < rows; r++) {
		const values = regionValues[r] ?? {};
		for (const layout of layouts) {
			// The recipe owning this param: the first recipe that declares it (param keys are
			// scoped per recipe; a region assignment references ONE recipe, so its row only ever
			// consults that recipe's layouts).
			const owner = recipes.find(rec => rec.params.some(p => p.key == layout.paramKey));
			const param = owner?.params.find(p => p.key == layout.paramKey);
			const def = param?.default;
			const v = values[layout.paramKey] ?? def;
			const base = (rowOffset + r) * columns + layout.column;
			if (layout.kind == "color") {
				const c = Array.isArray(v) ? v : (Array.isArray(def) ? def : [0, 0, 0]);
				for (let i = 0; i < 3; i++) out[base + i] = typeof c[i] == "number" ? c[i] : 0;
			} else {
				out[base] = typeof v == "number" ? v : (typeof def == "number" ? def : 0);
			}
		}
	}
	return out;
}

/** Set12 P3 — pack the per-region params texture payload WITH the P3 feather block prepended:
 *  each region row = [feather, curve, noiseAmp, noiseScale, opaque, …regionModulated params]
 *  (the REGION_FEATHER_* constants — MUST match the assembler's setRegionIndex fetches).
 *  Region row r+1 corresponds to map region r (row 0 = the base — feather defaults, never
 *  read meaningfully).  Feather/curve fall back to the map's `blendDefaults` when the region's
 *  feature leaves them absent, then to the class defaults.  `regionValues` is the P2-era
 *  per-row paramModulations map (row 0 = {} for the base); the feather block is overlaid
 *  AFTER packRegionParams so the param columns are untouched. */
export function packRegionParamsWithFeather(
	recipes: ResolvedRecipe[],
	rows: number,
	columns: number,
	regionValues: Array<{[paramKey: string]: ParamValue}>,
	regions: readonly MaterialRegion[],
	blendDefaults?: MaterialBlendDefaults,
): Float32Array {
	const out = packRegionParams(recipes, rows, columns, regionValues, 0);
	const defaults = blendDefaults ?? new MaterialBlendDefaults();
	for (let r = 0; r < regions.length; r++) {
		const mf = regions[r].pathFeature;
		const row = (r + 1) * columns;
		out[row + REGION_FEATHER_COLUMN] = mf.feather ?? defaults.feather ?? 2;
		// NOTE — parenthesise the curve fallback: `mf.featherCurve ?? defaults.featherCurve ==
		// null` parses as `mf.featherCurve ?? (defaults.featherCurve == null)` (== binds tighter
		// than ??) — a set feature curve would resolve to 0 (smooth).  The explicit local reads
		// cleanly and the absent → default → 0 mapping is unambiguous.
		const curve = mf.featherCurve ?? defaults.featherCurve;
		out[row + REGION_FEATHER_CURVE_COLUMN] = curve == null ? 0 : ["smooth", "linear", "step"].indexOf(curve);
		out[row + REGION_NOISE_AMP_COLUMN] = mf.edgeNoise?.amplitude ?? 0;
		out[row + REGION_NOISE_SCALE_COLUMN] = mf.edgeNoise?.scale ?? 1;
		out[row + REGION_OPAQUE_COLUMN] = mf.opaque ? 1 : 0;
	}
	return out;
}
